import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Calculator } from 'lucide-react';
import { usePi } from '../../contexts/PiContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const SendMoneyPage: React.FC = () => {
  const navigate = useNavigate();
  const { piBalance } = usePi();
  
  const [amount, setAmount] = useState<number>(10);
  const [recipient, setRecipient] = useState('');
  const [feeOption, setFeeOption] = useState<'sender_pays' | 'deducted'>('sender_pays');
  const [selectedMethod, setSelectedMethod] = useState('pi');

  const paymentMethods = [
    { id: 'pi', name: 'Pi Network', icon: '🥧', balance: piBalance },
    { id: 'paypal', name: 'PayPal', icon: '💙', balance: 150.00 },
    { id: 'googlepay', name: 'Google Pay', icon: '🟢', balance: 200.00 }
  ];

  const calculateFee = (amount: number) => {
    const percentageFee = 0.01 * amount; // 1%
    const flatFee = 0.10;
    return +(percentageFee + flatFee).toFixed(2);
  };

  const fee = calculateFee(amount);
  const senderPays = feeOption === 'sender_pays' ? +(amount + fee).toFixed(2) : amount;
  const receiverGets = feeOption === 'sender_pays' ? amount : +(amount - fee).toFixed(2);

  const handleSend = async () => {
    if (!recipient || amount <= 0) {
      alert('Please enter a valid recipient and amount');
      return;
    }

    const selectedPaymentMethod = paymentMethods.find(m => m.id === selectedMethod);
    if (!selectedPaymentMethod || selectedPaymentMethod.balance < senderPays) {
      alert('Insufficient balance in selected payment method');
      return;
    }

    try {
      // Simulate sending money
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`Successfully sent π ${amount} to ${recipient}!`);
      navigate('/payment-methods/transactions');
    } catch (error) {
      alert('Failed to send money. Please try again.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/payment-methods')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Send Money</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Recipient */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Send To</h3>
          <input
            type="text"
            placeholder="Enter recipient's username or email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </Card>

        {/* Amount */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Amount</h3>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">π</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              min="0.01"
              step="0.01"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-lg font-semibold"
            />
          </div>
        </Card>

        {/* Payment Method */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Payment Method</h3>
          <div className="space-y-2">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedMethod === method.id
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{method.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-800 dark:text-gray-100">{method.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Balance: π {method.balance.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    selectedMethod === method.id
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {selectedMethod === method.id && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Fee Options */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Fee Options</h3>
          <div className="space-y-3">
            <div
              onClick={() => setFeeOption('sender_pays')}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                feeOption === 'sender_pays'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-100">Sender pays fee on top</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Sender pays extra fee, receiver gets full amount</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 ${
                  feeOption === 'sender_pays'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {feeOption === 'sender_pays' && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
              </div>
            </div>

            <div
              onClick={() => setFeeOption('deducted')}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                feeOption === 'deducted'
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-100">Fee deducted from amount sent</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Sender pays stated amount, receiver gets less</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 ${
                  feeOption === 'deducted'
                    ? 'border-orange-500 bg-orange-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {feeOption === 'deducted' && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Calculation Summary */}
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={20} className="text-blue-600 dark:text-blue-400" />
            <h3 className="font-semibold text-blue-800 dark:text-blue-200">Transaction Summary</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-700 dark:text-blue-300">Fee:</span>
              <span className="font-semibold text-blue-800 dark:text-blue-200">π {fee}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700 dark:text-blue-300">Sender pays:</span>
              <span className="font-semibold text-blue-800 dark:text-blue-200">π {senderPays.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700 dark:text-blue-300">Receiver gets:</span>
              <span className="font-semibold text-blue-800 dark:text-blue-200">π {receiverGets.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          variant="gradient"
          size="lg"
          className="w-full"
        >
          <Send size={20} className="mr-2" />
          Send π {amount.toFixed(2)}
        </Button>
      </div>
    </div>
  );
};

export default SendMoneyPage;